import { Menu, Modal } from 'antd';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthTokenExpirationMonitor, useAuth } from '../../hooks/auth';
import { useDirty } from '../Blocker/dirty';
import { setIcon } from '../MapperIcon';
import { SidebarMenuOptions } from './menu';
import { Container } from './styles';

const Sidebar = ({ collapsed = false }) => {
    const navigate = useNavigate();
    const { isDirty, setClean } = useDirty();
    const { canAccessPage, user, updateUserPermission } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    
    // Use useMemo to prevent recalculation on every render
    // Only recalculate when user changes (using user object reference or key properties)
    const menuOptionData = useMemo(() => {
        return SidebarMenuOptions();
    }, [user?.userName, user?.customHomePage]);

    const [openKeys, setOpenKeys] = useState(['0']);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [rootSubmenuKeys, setRootSubmenuKeys] = useState([]);
    const [menuOptions, setMenuOptions] = useState(() => {
        const initialData = SidebarMenuOptions();
        return [...initialData];
    });

    // Helper function to find active menu item based on current pathname
    const findActiveMenuItem = (pathname, options) => {
        let bestMatch = null;
        let bestMatchLength = 0;

        // Normalize pathname
        const normalizedPath = pathname.endsWith('/') && pathname.length > 1 
            ? pathname.slice(0, -1) 
            : pathname;

        // First, check submenu items (more specific matches first)
        for (const option of options) {
            if (option.subMenu && option.subMenu.length > 0) {
                for (const subItem of option.subMenu) {
                    if (!subItem.url) continue;
                    
                    const subUrl = subItem.url.endsWith('/') && subItem.url.length > 1
                        ? subItem.url.slice(0, -1)
                        : subItem.url;

                    // Exact match (highest priority)
                    if (normalizedPath === subUrl) {
                        return { activeItem: subItem.key, parentKey: option.key };
                    }
                    
                    // Check if pathname starts with subItem.url (for nested routes)
                    if (normalizedPath.startsWith(subUrl) && subUrl !== '/') {
                        // Use the longest matching URL to avoid false positives
                        if (subUrl.length > bestMatchLength) {
                            bestMatch = { activeItem: subItem.key, parentKey: option.key };
                            bestMatchLength = subUrl.length;
                        }
                    }
                }
            }
        }

        // If we found a submenu match, return it
        if (bestMatch) {
            return bestMatch;
        }

        // Then check main menu items
        bestMatch = null;
        bestMatchLength = 0;

        for (const option of options) {
            if (!option.url) continue;
            
            const menuUrl = option.url.endsWith('/') && option.url.length > 1
                ? option.url.slice(0, -1)
                : option.url;

            // Exact match (highest priority)
            if (normalizedPath === menuUrl) {
                return { activeItem: option.key, parentKey: null };
            }
            
            // Check if pathname starts with option.url (for nested routes)
            if (normalizedPath.startsWith(menuUrl) && menuUrl !== '/') {
                // Use the longest matching URL to avoid false positives
                if (menuUrl.length > bestMatchLength) {
                    bestMatch = { activeItem: option.key, parentKey: null };
                    bestMatchLength = menuUrl.length;
                }
            }
        }

        // If we found a main menu match, return it
        if (bestMatch) {
            return bestMatch;
        }

        // Fallback: check for home route
        if (normalizedPath === '/' || normalizedPath === '/home' || normalizedPath === '/home/custom') {
            const homeOption = options.find(opt => {
                const optUrl = opt.url?.endsWith('/') && opt.url.length > 1
                    ? opt.url.slice(0, -1)
                    : opt.url;
                return optUrl === '/home' || optUrl === '/home/custom';
            });
            if (homeOption) {
                return { activeItem: homeOption.key, parentKey: null };
            }
        }

        return { activeItem: null, parentKey: null };
    };

    const tryNavigate = (path, newTab) => {
        if (!path) return;
        
        try {
            if (isDirty) {
                Modal.confirm({
                    content: t('Prompt.Leave'),
                    onOk: () => {
                        setClean();
                        if (newTab) {
                            window.open(path, '_blank');
                        } else {
                            navigate(path);
                        }
                    },
                    onCancel: () => {
                        // Do nothing if user cancels
                    },
                });
            } else {
                if (newTab) {
                    window.open(path, '_blank');
                } else {
                    navigate(path);
                }
            }
        } catch (error) {
            console.error('Navigation error:', error);
            // Fallback: try direct navigation
            if (!newTab) {
                window.location.href = path;
            }
        }
    };

    // Update menuOptions when menuOptionData changes
    useEffect(() => {
        if (menuOptionData && menuOptionData.length > 0) {
            // Only update if menuOptions is empty or if keys are different
            const currentKeys = menuOptions.map(m => m.key).join(',');
            const newKeys = menuOptionData.map(m => m.key).join(',');
            
            if (menuOptions.length === 0 || currentKeys !== newKeys) {
                setMenuOptions([...menuOptionData]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuOptionData]);

    useEffect(() => {
        if (menuOptions.length > 0) {
            const keys = menuOptions.map((opt) => opt.key);
            setRootSubmenuKeys(keys);
        }
    }, [menuOptions]);

    useEffect(() => {
        if (user.expirationData) {
            const interval = setInterval(async () => {
                await updateUserPermission();
            }, 1000000);
            return () => clearInterval(interval);
        }
    }, [user.expirationData, updateUserPermission]);

    function renderMenuItem(menuOption, showClassStyle) {
        const handleItemClick = (e) => {
            // Stop propagation to prevent double navigation
            e.stopPropagation();
            // Navigation will be handled by handleMenuClick, but ensure it works
            if (menuOption.url) {
                tryNavigate(menuOption.url, menuOption.newPage);
            }
        };

        return (
            <Menu.Item
                key={`${menuOption.key}`}
                icon={setIcon(menuOption.icon)}
                className={showClassStyle ? 'menu-item' : 'submenu-item'}
            >
                <span
                    onClick={handleItemClick}
                    style={{ cursor: 'pointer', width: '100%', display: 'block' }}
                >
                    {t(menuOption.name)}
                </span>
            </Menu.Item>
        );
    }

    function hasPermissionConfigRoute(subMenuData) {
        let subMenuPermissions = [];
        let RouteHasPermissionDefine = true;

        subMenuData.forEach((sub) => {
            if (sub.permission.length > 0) {
                sub.permission.forEach((p) => subMenuPermissions.push(p));
            } else {
                RouteHasPermissionDefine = false;
            }
        });

        return { subMenuPermissions, RouteHasPermissionDefine };
    }

    function renderSubMenu(menuOption) {
        // para submenus, mantemos só a checagem canAccessPage/submenus como antes
        const isAdmin = user?.userName?.toLowerCase() === 'admin';
        const { subMenuPermissions, RouteHasPermissionDefine } = hasPermissionConfigRoute(menuOption.subMenu);

        if (RouteHasPermissionDefine && !isAdmin && !canAccessPage(subMenuPermissions)) {
            return null;
        }

        return (
            <Menu.SubMenu key={menuOption.key} icon={setIcon(menuOption.icon)}>
                {mountSubMenuItems(menuOption.subMenu)}
            </Menu.SubMenu>
        );
    }

    function mountMenuItem(menuOption = null, showClassStyle = true) {
        if (!menuOption) return null;
        if (menuOption.name === 'Divider') {
            return (
                <Menu.Divider
                    key={menuOption.key}
                    style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.23)',
                        margin: '0 0 14px',
                    }}
                />
            );
        }

        AuthTokenExpirationMonitor; // apenas para disparar o hook

        const isAdmin = user?.userName?.toLowerCase() === 'admin';
        const isTenantPath = menuOption.url?.toLowerCase() === '/tenant';

        if (menuOption.permission.length > 0) {
            // exceção: admin sempre vê o /tenant
            if (isTenantPath && isAdmin) {
                return renderMenuItem(menuOption, showClassStyle);
            }
            // caso normal: só renderiza se tiver permissão
            if (canAccessPage(menuOption.permission)) {
                return renderMenuItem(menuOption, showClassStyle);
            }
            return null;
        }

        return renderMenuItem(menuOption, showClassStyle);
    }

    function mountSubMenuItems(subMenuItems = []) {
        return subMenuItems.map((sub) => mountMenuItem(sub, false));
    }

    useEffect(() => {
        const stored = localStorage.getItem('Flowy.MenuKey');
        if (stored) {
            setOpenKeys([stored]);
            setSelectedKeys([stored]);
        }
    }, []);

    // Update active menu item based on current route
    useEffect(() => {
        if (menuOptions.length > 0) {
            const { activeItem, parentKey } = findActiveMenuItem(location.pathname, menuOptions);
            
            if (activeItem) {
                // Update selected key only if different
                setSelectedKeys(prev => {
                    if (prev[0] !== activeItem) {
                        return [activeItem];
                    }
                    return prev;
                });
                
                // Update open keys if it's a submenu item
                if (parentKey) {
                    setOpenKeys(prev => {
                        if (prev[0] !== parentKey) {
                            localStorage.setItem('Flowy.MenuKey', parentKey);
                            return [parentKey];
                        }
                        return prev;
                    });
                } else {
                    // If it's a main menu item, update openKeys too
                    setOpenKeys(prev => {
                        if (prev[0] !== activeItem) {
                            localStorage.setItem('Flowy.MenuKey', activeItem);
                            return [activeItem];
                        }
                        return prev;
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, menuOptions]);

    const handleOpenChange = (keys) => {
        const latest = keys.find((k) => !openKeys.includes(k));
        setOpenKeys(
            rootSubmenuKeys.includes(latest) ? (latest ? [latest] : []) : keys
        );
    };

    const handleMenuClick = (e) => {
        const clickedKey = e.key;
        
        // Don't navigate if clicking on a submenu title (to expand/collapse)
        const isSubmenuTitle = menuOptions.some(opt => opt.key === clickedKey && opt.subMenu.length > 0);
        if (isSubmenuTitle) {
            // Just update openKeys, don't navigate
            return;
        }
        
        // Find the menu option that was clicked
        let clickedOption = menuOptions.find(opt => opt.key === clickedKey);
        let parentKey = null;
        
        // If not found in main menu, check submenus
        if (!clickedOption) {
            for (const option of menuOptions) {
                if (option.subMenu && option.subMenu.length > 0) {
                    clickedOption = option.subMenu.find(sub => sub.key === clickedKey);
                    if (clickedOption) {
                        parentKey = option.key;
                        // If it's a submenu item, open the parent submenu
                        setOpenKeys([option.key]);
                        break;
                    }
                }
            }
        }
        
        if (clickedOption && clickedOption.url) {
            // Update selected key
            setSelectedKeys([clickedKey]);
            localStorage.setItem('Flowy.MenuKey', clickedKey);
            
            // Navigate to the URL
            tryNavigate(clickedOption.url, clickedOption.newPage);
            
            // If clicking a main menu item (not a submenu), update openKeys
            if (!parentKey) {
                setOpenKeys([clickedKey]);
            }
        }
    };

    return (
        <Container>
            <Menu
                theme="dark"
                mode="inline"
                style={{ width: collapsed ? 55 : '100%' }}
                onClick={handleMenuClick}
                openKeys={openKeys}
                selectedKeys={selectedKeys}
                onOpenChange={handleOpenChange}
            >
                {menuOptions.map((opt) =>
                    opt.subMenu.length > 0 ? renderSubMenu(opt) : mountMenuItem(opt)
                )}
            </Menu>
        </Container>
    );
};

export default Sidebar;
